import * as React from "react";
import { useDataProvider } from "react-admin";
import type { Cargo } from "../models/Cargo";
import type { VerifyRecord } from "../models/common";
import type { TicketListItem } from "../models/Ticket";
import type { Waybill } from "../models/Waybill";
import type { AppDataProvider } from "../types/dataProvider";

/** 列表更新通知类型 */
export type ListUpdateType =
  | "communication"
  | "cargo"
  | "waybill"
  | "verifyUser";

/** 右下角通知条目 */
export interface ListUpdateNotificationItem {
  /** 通知唯一 ID */
  id: string;
  /** 业务记录 ID */
  recordId: string;
  /** 业务类型 */
  type: ListUpdateType;
  /** 展示标题 */
  title: string;
  /** 点击跳转详情路径 */
  path: string;
  /** 类型中文前缀 */
  typeLabel: string;
  /** 入队时间戳 */
  createdAt: number;
}

/** 同类型堆叠分组 */
export interface ListUpdateNotificationGroup {
  type: ListUpdateType;
  typeLabel: string;
  items: ListUpdateNotificationItem[];
}

interface PollHeadItem {
  id: string;
  title: string;
}

interface PollSourceConfig {
  type: ListUpdateType;
  typeLabel: string;
  buildDetailPath: (recordId: string) => string;
  fetchHeads: (dataProvider: AppDataProvider) => Promise<PollHeadItem[]>;
}

/** 轮询间隔（毫秒） */
export const POLL_INTERVAL_MS = 30_000;

/** 轮询运行状态 */
export type ListUpdatePollStatus = "idle" | "polling" | "paused";

/**
 * 从沟通工单列表取按 updatedAt 降序的条目。
 */
const fetchCommunicationHeads = async (
  dataProvider: AppDataProvider
): Promise<PollHeadItem[]> => {
  const response = await dataProvider.getTickets();
  const sorted = [...response.data].sort((a, b) => {
    if (a.updatedAt && b.updatedAt) {
      return b.updatedAt.localeCompare(a.updatedAt);
    }
    return 0;
  });
  return sorted
    .filter((item): item is TicketListItem => Boolean(item?.id))
    .slice(0, 10)
    .map((item) => ({
      id: String(item.id),
      title: item.title?.trim() || "沟通工单",
    }));
};

/**
 * 从货源列表取第一页（上架中、id 降序）。
 */
const fetchCargoHeads = async (
  dataProvider: AppDataProvider
): Promise<PollHeadItem[]> => {
  const response = await dataProvider.getList("validCargos", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "id", order: "DESC" },
    filter: { status: 1 },
  });
  return (response.data as Cargo[])
    .filter((item) => Boolean(item?.id))
    .map((item) => ({
      id: String(item.id),
      title: item.name?.trim() || `货源 ${item.id}`,
    }));
};

/**
 * 从运单列表取第一页（id 降序）。
 */
const fetchWaybillHeads = async (
  dataProvider: AppDataProvider
): Promise<PollHeadItem[]> => {
  const response = await dataProvider.getList("waybills", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "id", order: "DESC" },
    filter: {},
  });
  return (response.data as Waybill[])
    .filter((item) => Boolean(item?.id))
    .map((item) => {
      const cargoName = item.cargo?.name?.trim();
      return {
        id: String(item.id),
        title: cargoName || `运单 ${item.id}`,
      };
    });
};

/**
 * 从用户认证列表取第一页（createdAt 降序）。
 */
const fetchVerifyUserHeads = async (
  dataProvider: AppDataProvider
): Promise<PollHeadItem[]> => {
  const response = await dataProvider.getList("verify/user", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "createdAt", order: "DESC" },
    filter: {},
  });
  return (response.data as VerifyRecord[])
    .filter((item) => Boolean(item?.id))
    .map((item) => ({
      id: String(item.id),
      title: item.name?.trim() || `认证 ${item.id}`,
    }));
};

const POLL_SOURCES: PollSourceConfig[] = [
  {
    type: "communication",
    typeLabel: "沟通",
    buildDetailPath: (recordId) => `/communication/${recordId}`,
    fetchHeads: fetchCommunicationHeads,
  },
  {
    type: "cargo",
    typeLabel: "货源",
    buildDetailPath: (recordId) => `/validCargos/${recordId}/show`,
    fetchHeads: fetchCargoHeads,
  },
  {
    type: "waybill",
    typeLabel: "订单",
    buildDetailPath: (recordId) => `/waybills/${recordId}/show`,
    fetchHeads: fetchWaybillHeads,
  },
  {
    type: "verifyUser",
    typeLabel: "认证",
    buildDetailPath: (recordId) =>
      `/verify/user?verifyId=${encodeURIComponent(recordId)}`,
    fetchHeads: fetchVerifyUserHeads,
  },
];

/**
 * 按类型分组未读通知，组内按入队时间新到旧。
 */
export const groupNotificationsByType = (
  items: ListUpdateNotificationItem[]
): ListUpdateNotificationGroup[] => {
  const order = POLL_SOURCES.map((source) => source.type);
  const map = new Map<ListUpdateType, ListUpdateNotificationItem[]>();

  items.forEach((item) => {
    const list = map.get(item.type) ?? [];
    list.push(item);
    map.set(item.type, list);
  });

  return order
    .filter((type) => (map.get(type)?.length ?? 0) > 0)
    .map((type) => {
      const groupItems = [...(map.get(type) ?? [])].sort(
        (a, b) => b.createdAt - a.createdAt
      );
      return {
        type,
        typeLabel: groupItems[0]?.typeLabel ?? type,
        items: groupItems,
      };
    });
};

/**
 * 定时轮询四类列表数据，累计未读通知；首次仅建立首条基线。
 */
export const useListUpdatePolling = () => {
  const dataProvider = useDataProvider() as AppDataProvider;
  const lastHeadIdRef = React.useRef<Partial<Record<ListUpdateType, string>>>(
    {}
  );
  const initializedRef = React.useRef(false);
  const pollingRef = React.useRef(false);
  const [queue, setQueue] = React.useState<ListUpdateNotificationItem[]>([]);
  const [status, setStatus] = React.useState<ListUpdatePollStatus>("idle");
  const [lastPolledAt, setLastPolledAt] = React.useState<number | null>(null);
  const [nextPollAt, setNextPollAt] = React.useState<number | null>(null);

  /**
   * 标记通知已读并从 UI 队列移除。
   */
  const markAsRead = React.useCallback((id: string) => {
    setQueue((prev) => prev.filter((item) => item.id !== id));
  }, []);

  /**
   * 执行一轮轮询：相对上次首条截取新增条目并入未读队列。
   */
  const pollOnce = React.useCallback(async () => {
    if (
      typeof document !== "undefined" &&
      document.visibilityState === "hidden"
    ) {
      setStatus("paused");
      return;
    }

    if (pollingRef.current) {
      return;
    }

    pollingRef.current = true;
    setStatus("polling");

    try {
      const results = await Promise.allSettled(
        POLL_SOURCES.map(async (source) => {
          const heads = await source.fetchHeads(dataProvider);
          return { source, heads };
        })
      );

      const isFirstPass = !initializedRef.current;
      const nextNotifications: ListUpdateNotificationItem[] = [];
      const now = Date.now();

      results.forEach((result, index) => {
        const source = POLL_SOURCES[index];
        if (result.status === "rejected") {
          console.warn(
            `[list-update-polling] ${source.type} 轮询失败`,
            result.reason
          );
          return;
        }

        const { heads } = result.value;
        if (!heads.length) {
          return;
        }

        const previousHeadId = lastHeadIdRef.current[source.type];
        lastHeadIdRef.current[source.type] = heads[0].id;

        if (isFirstPass) {
          return;
        }

        for (let i = 0; i < heads.length; i += 1) {
          const head = heads[i];
          if (previousHeadId && head.id === previousHeadId) {
            break;
          }
          nextNotifications.push({
            id: `${source.type}-${head.id}-${now}-${i}`,
            recordId: head.id,
            type: source.type,
            title: head.title,
            path: source.buildDetailPath(head.id),
            typeLabel: source.typeLabel,
            createdAt: now + (heads.length - i),
          });
        }
      });

      initializedRef.current = true;

      if (nextNotifications.length > 0) {
        setQueue((prev) => {
          const existingKeys = new Set(
            prev.map((item) => `${item.type}:${item.recordId}`)
          );
          const deduped = nextNotifications.filter(
            (item) => !existingKeys.has(`${item.type}:${item.recordId}`)
          );
          return deduped.length > 0 ? [...prev, ...deduped] : prev;
        });
      }

      setLastPolledAt(now);
      setNextPollAt(now + POLL_INTERVAL_MS);
      setStatus("idle");
    } catch (error) {
      console.warn("[list-update-polling] 轮询异常", error);
      const now = Date.now();
      setLastPolledAt(now);
      setNextPollAt(now + POLL_INTERVAL_MS);
      setStatus("idle");
    } finally {
      pollingRef.current = false;
    }
  }, [dataProvider]);

  React.useEffect(() => {
    void pollOnce();
    const timerId = window.setInterval(() => {
      void pollOnce();
    }, POLL_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        setStatus((prev) => (prev === "polling" ? prev : "paused"));
        return;
      }
      setStatus((prev) => (prev === "paused" ? "idle" : prev));
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(timerId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [pollOnce]);

  const groups = React.useMemo(
    () => groupNotificationsByType(queue),
    [queue]
  );

  return {
    queue,
    groups,
    markAsRead,
    status,
    lastPolledAt,
    nextPollAt,
    pollIntervalMs: POLL_INTERVAL_MS,
  };
};
